import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Zap, MessageSquare, Star, PhoneCall, BarChart3, GraduationCap, Clock, Users, Bot, Mail, TrendingUp, Award, Phone, Sparkles, HelpCircle, Database, Target, Workflow, Wrench } from "lucide-react";
import PresentationDownloadButton from "../components/PresentationDownloadButton";

const CALLREVU_LOGO = "https://www.callrevu.com/wp-content/uploads/2024/12/CR_Logo_Wordmark_White_RGB.png";
const CALLDRIP_LOGO = "https://www.calldrip.com/hs-fs/hubfs/calldrip-logo-white.png?width=280&height=70&name=calldrip-logo-white.png";

const slides = [
  {
    id: 1,
    type: "title",
    content: {
      eyebrow: "MULTI-CHANNEL COMMUNICATION PLATFORM",
      headline: "Effortlessly Maximize",
      highlight: "Your Sales Team's Potential",
      tagline: "Rapid Lead Response · AI Sales Assistant · Scorecard & Coaching"
    }
  },
  {
    id: 2,
    type: "discovery",
    content: {
      label: "BEFORE WE DIVE IN",
      headline: "A Few Questions For You",
      subhead: "We want this demo to be relevant — not a generic pitch.",
      questions: [
        { icon: "database", q: "What CRM do you use?" },
        { icon: "trending", q: "Roughly how many internet lead opportunities do you handle per month?" },
        { icon: "workflow", q: "What is your current process to respond to them?", hint: "Internet team · BDC · Round Robin · Jump Ball?" },
        { icon: "target", q: "Do you track your lead conversion rate?" },
        { icon: "wrench", q: "If you could fix anything about this process, what would it be?" }
      ]
    }
  },
  {
    id: 3,
    type: "background",
    content: {
      label: "BACKGROUND",
      headline: "We've Been Doing This A While",
      stats: [
        { value: "6K+", label: "Companies served" },
        { value: "15+", label: "Years in market" },
        { value: "30M+", label: "Conversations driven" }
      ],
      logos: [
        "Hyundai", "Nissan", "GM", "Toyota", "BMW",
        "Land Rover", "Berkshire Hathaway", "Ken Garff", "Honda", "Ford",
        "Costco", "Lookers", "Vauxhall", "Renault", "Fletcher Auto",
        "CarGurus", "Mercedes-Benz", "Audi", "Lamborghini", "Aston Martin"
      ]
    }
  },
  {
    id: 4,
    type: "tools",
    content: {
      label: "THE PLATFORM",
      headline: "Tools to Maximize Your Sales Team",
      tools: [
        { icon: "clock", title: "Rapid Lead Response", desc: "Personally respond to every lead in seconds via text & phone", color: "#06b6d4" },
        { icon: "message", title: "SMS Platform", desc: "Shared inbox + virtual assistants. Broadcast texts to lists.", color: "#3b82f6" },
        { icon: "star", title: "Review Boost", desc: "Improve your Google ranking with an automated review process", color: "#f59e0b" },
        { icon: "phone", title: "Call Tracking", desc: "Engage instantly while gathering source insights", color: "#8b5cf6" },
        { icon: "chart", title: "Scorecard", desc: "AI-powered scoring on every conversation — pinpoint what to fix", color: "#22c55e" },
        { icon: "grad", title: "Sales Coaching", desc: "Built-in AI scoring + coaching tools to level up your team", color: "#ef4444" }
      ]
    }
  },
  {
    id: 5,
    type: "rapidResponse",
    content: {
      label: "RAPID LEAD RESPONSE",
      headline: "Speed Wins. Every Time.",
      points: [
        "The best time to engage a prospect is at their peak moment of interest — while they're actively shopping",
        "Calldrip automates engagement to maximize speed AND keep the human element",
        "Phone, text, chat, lead form — your salesperson is automatically connected almost instantly, anywhere",
        "Your rep is on the phone with the consumer before your competition even knows the lead exists"
      ],
      stats: [
        { big: "<1 min", label: "Calldrip response time" },
        { big: "5 min", label: "Best window to respond" },
        { big: "10x", label: "Drop in engagement after 5 min" },
        { big: "65%+", label: "Lead connection rate" }
      ]
    }
  },
  {
    id: 6,
    type: "workflow",
    content: {
      label: "HOW IT WORKS",
      headline: "Internet Lead Inquiry — Step by Step",
      steps: [
        { num: "01", title: "Lead Drops In", desc: "Web form, chat, call, or CRM push — we capture it the moment it arrives", icon: "zap" },
        { num: "02", title: "Contact Info Alert", desc: "We share the lead with the right consultant via our priority alert process", icon: "phone" },
        { num: "03", title: "Auto-Connect", desc: "Shopper is immediately bridged to the consultant — almost instantly", icon: "users" },
        { num: "04", title: "AI Listens & Scores", desc: "Conversational intelligence monitors every call to deliver actionable insights", icon: "bot" }
      ],
      callout: "9:46 am — Jim booked a 2pm appointment. Tagged: Appt Set · Good Sentiment"
    }
  },
  {
    id: 7,
    type: "aiAssistant",
    content: {
      label: "AI SALES ASSISTANT",
      headline: "When the Human Misses, AI Doesn't",
      flow: [
        { title: "Conversation Doesn't Happen", desc: "Rep busy, after-hours, or holiday — lead would normally go cold", color: "#ef4444" },
        { title: "AI Engages Instantly", desc: "Whitney (or your branded persona) texts the shopper and starts qualifying", color: "#3b82f6" },
        { title: "Appointment Booked", desc: "Lead is nurtured, qualified, and scheduled — synced to your CRM, app, email", color: "#22c55e" }
      ],
      proofPoint: "22% of conversations led by Calldrip's Virtual Assistant turn into a sales appointment.",
      sample: "\"Hi there! My name is Whitney, sales agent at All-Star Auto. We have the Ford F-150 in stock — would you like to schedule a time to come take a look?\""
    }
  },
  {
    id: 8,
    type: "smsPlatform",
    content: {
      label: "SMS PLATFORM",
      headline: "Always-On Customer Conversations",
      points: [
        "Even your best reps aren't 24/7 — but buyers shop at all hours",
        "Calldrip's virtual assistant engages and books appointments around the clock",
        "Personalized nurture sequences hand off to a real rep when the lead is qualified",
        "Shared inbox keeps every team member on the same page — full transparency"
      ],
      preview: {
        contact: "Sally Smith",
        msgs: [
          { type: "auto", text: "Hi — this is All Star Mitsubishi. We received your inquiry and will reach out shortly. Reply STOP to opt out." },
          { type: "system", text: "Jim Lewis placed a call · 5:43 PM" },
          { type: "system", text: "Conversation assigned to Jim Lewis" },
          { type: "agent", text: "Hi Sally — great talking with you. Confirming your test drive for tomorrow at 10am." }
        ]
      }
    }
  },
  {
    id: 9,
    type: "reviewBoost",
    content: {
      label: "REVIEW BOOST",
      headline: "Turn Happy Customers Into Google Reviews",
      steps: [
        { title: "Customer Contact Confirmation", desc: "We capture the rep + interaction context", icon: "phone" },
        { title: "Quick Sentiment Check", desc: "Smiley-scale rating to filter happy from unhappy", icon: "star" },
        { title: "Direct-to-Google Submit", desc: "Happy customers are routed directly to your Google profile", icon: "trending" }
      ],
      result: "Higher star ratings · More volume · Better local rank"
    }
  },
  {
    id: 10,
    type: "callTracking",
    content: {
      label: "CALL TRACKING",
      headline: "Every Inbound Call. Every Source. Tracked.",
      features: [
        { title: "Branded Caller ID", desc: "Higher answer rates", icon: "phone" },
        { title: "Built-in IVR", desc: "Route to the right team", icon: "workflow" },
        { title: "Integrated SMS + Virtual Assistant", desc: "No call missed", icon: "message" },
        { title: "Schedules & Escalations", desc: "After-hours and overflow handled", icon: "clock" },
        { title: "Powered by Calldrip Scorecards", desc: "Every call AI-scored", icon: "chart" },
        { title: "Multi-Channel Reporting", desc: "Source attribution on every conversation", icon: "trending" }
      ]
    }
  },
  {
    id: 11,
    type: "scorecard",
    content: {
      label: "SCORECARD",
      headline: "AI-Powered Conversation Scoring",
      points: [
        "Built-in AI monitors, scores, and identifies improvement opportunities — automatically",
        "Save A Deal · Calldrip Second Chance: catch missed opportunities before they're gone",
        "AI-generated coaching recommendations boost appointment sets by up to 45%"
      ],
      sample: {
        rep: "Kevin Andersen",
        score: 71,
        items: [
          { name: "Intro Script", alert: true, opp: true },
          { name: "Feature Inquiry", alert: true, opp: true },
          { name: "Trade Inquiry", alert: false, opp: true },
          { name: "Asked For Appointment", alert: true, opp: true },
          { name: "Sell The Dealership", alert: true, opp: true },
          { name: "Appointment Set", alert: true, opp: true },
          { name: "Save A Deal", alert: false, opp: true }
        ]
      }
    }
  },
  {
    id: 12,
    type: "multiScorecard",
    content: {
      label: "TEAM-LEVEL VISIBILITY",
      headline: "Multiple Scorecards · Proactive Coaching",
      kpis: [
        { name: "Control The Conversation", value: 78, goal: 80 },
        { name: "Selling The Sizzle", value: 65, goal: 75 },
        { name: "Inventory Options", value: 82, goal: 80 },
        { name: "Trade-In Vehicle", value: 54, goal: 70 },
        { name: "Attempted Appointment", value: 71, goal: 85 },
        { name: "Appointment Set", value: 66, goal: 70 }
      ],
      summary: [
        { label: "Scored Calls", value: "15", goal: "90%" },
        { label: "Opportunities", value: "20%", goal: "75%" },
        { label: "Appt Set Rate", value: "66%", goal: "70%" }
      ]
    }
  },
  {
    id: 13,
    type: "coaching",
    content: {
      label: "SALES COACHING",
      headline: "Know · Measure · Coach · Improve",
      points: [
        "See exactly how every consultant is performing — at a glance",
        "Spot the missed opportunities and the conversations that need fixing",
        "AI-flagged calls trigger 'YOUR CALL HAS BEEN COACHED' notifications with one-click playback"
      ],
      pillars: [
        { title: "Know", desc: "Real-time team performance dashboards" },
        { title: "Measure", desc: "Quality + quantity, scored by AI" },
        { title: "Coach", desc: "Targeted feedback on the calls that matter" },
        { title: "Improve", desc: "Repeatable habits that compound week over week" }
      ]
    }
  },
  {
    id: 14,
    type: "demo",
    content: {
      label: "LIVE DEMO",
      headline: "Let's See It In Action",
      sub: "Submit a lead — watch the magic happen.",
      cards: [
        { vehicle: "Ford F-150", trim: "Lariat 4x4", desc: "Standard inbound demo flow" },
        { vehicle: "Lamborghini Huracán", trim: "EVO Spyder", desc: "High-intent internet-lead flow" }
      ]
    }
  },
  {
    id: 15,
    type: "closing",
    content: {
      headline: "Let's Build This",
      highlight: "For Your Team",
      message: "Speed. Scoring. Coaching. All in one platform.",
      cta: "Ready to talk next steps?"
    }
  }
];

// ===================== Icon Helper =====================
const IconRenderer = ({ name, size = 24, className = "", style = {} }) => {
  const icons = {
    clock: Clock, message: MessageSquare, star: Star, phone: Phone,
    chart: BarChart3, grad: GraduationCap, zap: Zap, users: Users,
    bot: Bot, mail: Mail, trending: TrendingUp, award: Award,
    sparkles: Sparkles, help: HelpCircle, database: Database,
    target: Target, workflow: Workflow, wrench: Wrench, phoneCall: PhoneCall
  };
  const Icon = icons[name] || Sparkles;
  return <Icon size={size} className={className} style={style} />;
};

// ===================== Slide Renderers =====================
const TitleSlide = ({ content }) => (
  <div className="flex flex-col items-center justify-center h-full text-center px-8 pt-12">
    <div className="flex items-center gap-8 mb-12">
      <img src={CALLREVU_LOGO} alt="CallRevu" className="h-12 opacity-90" />
      <div className="w-px h-10 bg-white/20" />
      <img src={CALLDRIP_LOGO} alt="Calldrip" className="h-10 opacity-90" />
    </div>
    <div className="text-cyan-400 text-sm font-semibold tracking-[0.3em] mb-6">{content.eyebrow}</div>
    <h1 className="text-4xl md:text-6xl font-light text-white mb-2">{content.headline}</h1>
    <h1 className="text-5xl md:text-7xl font-bold text-cyan-400 mb-10">{content.highlight}</h1>
    <p className="text-lg text-white/60 max-w-3xl">{content.tagline}</p>
  </div>
);

const DiscoverySlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-10">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-3" data-testid="discovery-label">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-3">{content.headline}</h2>
    <p className="text-white/60 text-lg mb-8">{content.subhead}</p>
    <div className="flex-1 grid grid-cols-1 gap-4">
      {content.questions.map((item, i) => (
        <div
          key={i}
          className="flex items-start gap-5 p-5 rounded-xl bg-white/[0.03] border border-white/10 hover:border-cyan-500/40 hover:bg-white/[0.06] transition-all"
          data-testid={`discovery-question-${i + 1}`}
        >
          <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
            <IconRenderer name={item.icon} size={22} className="text-cyan-400" />
          </div>
          <div className="flex-1 pt-1">
            <p className="text-xl text-white font-medium leading-tight">
              <span className="text-cyan-400 mr-2">{String(i + 1).padStart(2, "0")}.</span>
              {item.q}
            </p>
            {item.hint && (
              <p className="text-white/50 text-sm mt-1.5 italic">{item.hint}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const BackgroundSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-10">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-3">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="grid grid-cols-3 gap-6 mb-10">
      {content.stats.map((s, i) => (
        <div key={i} className="rounded-xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 p-6 text-center">
          <div className="text-5xl md:text-6xl font-bold text-cyan-400 mb-2">{s.value}</div>
          <div className="text-white/70 text-sm uppercase tracking-wider">{s.label}</div>
        </div>
      ))}
    </div>
    <div className="flex-1">
      <div className="text-white/50 text-xs uppercase tracking-widest mb-3">Trusted by</div>
      <div className="grid grid-cols-5 gap-3">
        {content.logos.map((logo, i) => (
          <div key={i} className="rounded-lg bg-white/[0.03] border border-white/10 px-3 py-3 text-center text-white/70 text-xs font-medium hover:bg-white/[0.06] transition-all">
            {logo}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ToolsSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-10">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-3">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-3 gap-5">
      {content.tools.map((t, i) => (
        <div key={i} className="rounded-xl p-6 border bg-white/[0.03] hover:bg-white/[0.06] transition-all" style={{ borderColor: t.color + "40" }}>
          <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: t.color + "20", border: `1px solid ${t.color}60` }}>
            <IconRenderer name={t.icon} size={24} style={{ color: t.color }} />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">{t.title}</h3>
          <p className="text-white/60 text-sm leading-relaxed">{t.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const RapidResponseSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-10">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-3">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-2 gap-8">
      <div className="space-y-4">
        {content.points.map((p, i) => (
          <div key={i} className="flex gap-3">
            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center text-cyan-400 font-bold text-sm">{i + 1}</div>
            <p className="text-white/85 text-base leading-relaxed">{p}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4 content-start">
        {content.stats.map((s, i) => (
          <div key={i} className="rounded-xl bg-gradient-to-br from-cyan-500/10 to-blue-600/10 border border-cyan-500/30 p-5 text-center">
            <div className="text-4xl font-bold text-cyan-400 mb-1">{s.big}</div>
            <div className="text-white/70 text-xs uppercase tracking-wider leading-tight">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const WorkflowSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-10">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-3">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-4 gap-4">
      {content.steps.map((s, i) => (
        <div key={i} className="relative rounded-xl bg-white/[0.03] border border-white/10 p-5 flex flex-col">
          <div className="text-cyan-400 text-3xl font-bold mb-3">{s.num}</div>
          <div className="w-10 h-10 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center mb-3">
            <IconRenderer name={s.icon} size={20} className="text-cyan-400" />
          </div>
          <h3 className="text-lg font-bold text-white mb-2">{s.title}</h3>
          <p className="text-white/60 text-sm leading-snug">{s.desc}</p>
        </div>
      ))}
    </div>
    <div className="mt-6 rounded-xl bg-gradient-to-r from-green-500/15 to-emerald-500/15 border border-green-500/30 p-4 flex items-center gap-3">
      <Sparkles size={20} className="text-green-400" />
      <p className="text-white font-medium">{content.callout}</p>
    </div>
  </div>
);

const AIAssistantSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-10">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-3">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{content.headline}</h2>
    <div className="grid grid-cols-3 gap-4 mb-6">
      {content.flow.map((f, i) => (
        <div key={i} className="rounded-xl p-5 border" style={{ backgroundColor: f.color + "12", borderColor: f.color + "50" }}>
          <div className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: f.color }}>Step {i + 1}</div>
          <h3 className="text-lg font-bold text-white mb-2">{f.title}</h3>
          <p className="text-white/65 text-sm leading-relaxed">{f.desc}</p>
        </div>
      ))}
    </div>
    <div className="rounded-xl bg-white/[0.04] border border-white/10 p-5 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Bot size={18} className="text-cyan-400" />
        <span className="text-cyan-400 text-xs font-semibold tracking-widest uppercase">AI Sample</span>
      </div>
      <p className="text-white/85 italic">{content.sample}</p>
    </div>
    <div className="rounded-xl bg-gradient-to-r from-green-500/15 to-emerald-500/15 border border-green-500/30 p-4 text-center">
      <p className="text-white text-lg font-semibold">{content.proofPoint}</p>
    </div>
  </div>
);

const SMSPlatformSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-10">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-3">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-2 gap-10">
      <div className="space-y-4">
        {content.points.map((p, i) => (
          <div key={i} className="flex gap-3">
            <MessageSquare size={20} className="text-cyan-400 flex-shrink-0 mt-1" />
            <p className="text-white/85 text-base leading-relaxed">{p}</p>
          </div>
        ))}
      </div>
      <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
          <div>
            <div className="text-white font-bold">{content.preview.contact}</div>
            <div className="text-white/40 text-xs">via Calldrip</div>
          </div>
          <Phone size={18} className="text-cyan-400" />
        </div>
        <div className="space-y-3">
          {content.preview.msgs.map((m, i) => (
            <div key={i}>
              {m.type === "system" ? (
                <div className="text-center text-white/40 text-xs italic">— {m.text} —</div>
              ) : (
                <div className={`flex ${m.type === "agent" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm ${m.type === "agent" ? "bg-cyan-500 text-white rounded-br-sm" : "bg-white/10 text-white rounded-bl-sm"}`}>
                    {m.text}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const ReviewBoostSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-10">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-3">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-3 gap-6">
      {content.steps.map((s, i) => (
        <div key={i} className="relative rounded-xl bg-white/[0.04] border border-white/10 p-6 flex flex-col">
          <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold">{i + 1}</div>
          <div className="w-12 h-12 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mb-4 mt-2">
            <IconRenderer name={s.icon} size={22} className="text-amber-400" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">{s.title}</h3>
          <p className="text-white/60 text-sm leading-relaxed">{s.desc}</p>
        </div>
      ))}
    </div>
    <div className="mt-6 rounded-xl bg-gradient-to-r from-amber-500/15 to-orange-500/15 border border-amber-500/30 p-4 text-center">
      <div className="flex items-center justify-center gap-2">
        <Star size={20} className="text-amber-400 fill-amber-400" />
        <p className="text-white text-lg font-semibold">{content.result}</p>
      </div>
    </div>
  </div>
);

const CallTrackingSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-10">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-3">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-3 gap-5">
      {content.features.map((f, i) => (
        <div key={i} className="rounded-xl bg-white/[0.03] border border-white/10 p-5 hover:border-purple-500/40 transition-all">
          <div className="w-10 h-10 rounded-lg bg-purple-500/15 border border-purple-500/30 flex items-center justify-center mb-3">
            <IconRenderer name={f.icon} size={20} className="text-purple-400" />
          </div>
          <h3 className="text-lg font-bold text-white mb-1">{f.title}</h3>
          <p className="text-white/60 text-sm">{f.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const ScorecardSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-10">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-3">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-2 gap-8">
      <div className="space-y-5">
        {content.points.map((p, i) => (
          <div key={i} className="flex gap-3">
            <BarChart3 size={20} className="text-green-400 flex-shrink-0 mt-1" />
            <p className="text-white/85 text-base leading-relaxed">{p}</p>
          </div>
        ))}
      </div>
      <div className="rounded-xl bg-white/[0.04] border border-white/10 p-5">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
          <div>
            <div className="text-white/50 text-xs uppercase tracking-widest">Scorecard</div>
            <div className="text-white font-bold text-lg">{content.sample.rep}</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-400">{content.sample.score}</div>
            <div className="text-white/50 text-xs">/ 100</div>
          </div>
        </div>
        <div className="space-y-2">
          {content.sample.items.map((item, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
              <span className="text-white/80 text-sm">{item.name}</span>
              <div className="flex gap-2">
                <span className={`text-xs px-2 py-0.5 rounded ${item.alert ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                  {item.alert ? "YES" : "NO"}
                </span>
                <span className="text-xs px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400">OPP</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const MultiScorecardSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-10">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-3">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{content.headline}</h2>
    <div className="grid grid-cols-3 gap-4 mb-6">
      {content.summary.map((s, i) => (
        <div key={i} className="rounded-xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 p-4">
          <div className="text-white/60 text-xs uppercase tracking-widest mb-1">{s.label}</div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-cyan-400">{s.value}</span>
            <span className="text-white/40 text-xs">goal {s.goal}</span>
          </div>
        </div>
      ))}
    </div>
    <div className="flex-1 grid grid-cols-3 gap-4">
      {content.kpis.map((k, i) => {
        const hit = k.value >= k.goal;
        return (
          <div key={i} className="rounded-xl bg-white/[0.03] border border-white/10 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80 text-sm font-medium">{k.name}</span>
              <span className={`text-xs font-bold ${hit ? "text-green-400" : "text-amber-400"}`}>{k.value}%</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${hit ? "bg-green-400" : "bg-amber-400"}`} style={{ width: `${k.value}%` }} />
            </div>
            <div className="text-white/40 text-xs mt-1">Goal: {k.goal}%</div>
          </div>
        );
      })}
    </div>
  </div>
);

const CoachingSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-10">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-3">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{content.headline}</h2>
    <div className="space-y-4 mb-8">
      {content.points.map((p, i) => (
        <div key={i} className="flex gap-3">
          <GraduationCap size={22} className="text-red-400 flex-shrink-0 mt-1" />
          <p className="text-white/85 text-base leading-relaxed">{p}</p>
        </div>
      ))}
    </div>
    <div className="flex-1 grid grid-cols-4 gap-4">
      {content.pillars.map((p, i) => (
        <div key={i} className="rounded-xl bg-gradient-to-br from-red-500/10 to-rose-500/10 border border-red-500/30 p-5 flex flex-col">
          <div className="text-red-400 text-xs font-bold tracking-widest mb-2">0{i + 1}</div>
          <h3 className="text-2xl font-bold text-white mb-2">{p.title}</h3>
          <p className="text-white/65 text-sm">{p.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const DemoSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-10">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-3">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-3">{content.headline}</h2>
    <p className="text-white/60 text-lg mb-10">{content.sub}</p>
    <div className="flex-1 grid grid-cols-2 gap-6">
      {content.cards.map((c, i) => (
        <div key={i} className="rounded-2xl bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/15 p-8 flex flex-col justify-between">
          <div>
            <div className="text-cyan-400 text-xs font-semibold tracking-widest mb-2">VEHICLE {i + 1}</div>
            <h3 className="text-3xl font-bold text-white mb-1">{c.vehicle}</h3>
            <p className="text-white/60 mb-4">{c.trim}</p>
            <p className="text-white/50 text-sm italic">{c.desc}</p>
          </div>
          <button className="mt-6 px-5 py-3 bg-cyan-500 hover:bg-cyan-400 text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-2 group">
            Submit Lead
            <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      ))}
    </div>
  </div>
);

const ClosingSlide = ({ content }) => (
  <div className="flex flex-col items-center justify-center h-full text-center px-8 pt-12">
    <div className="flex items-center gap-8 mb-12">
      <img src={CALLREVU_LOGO} alt="CallRevu" className="h-12 opacity-90" />
      <div className="w-px h-10 bg-white/20" />
      <img src={CALLDRIP_LOGO} alt="Calldrip" className="h-10 opacity-90" />
    </div>
    <h1 className="text-4xl md:text-5xl font-light text-white mb-2">{content.headline}</h1>
    <h1 className="text-5xl md:text-6xl font-bold text-cyan-400 mb-8">{content.highlight}</h1>
    <p className="text-xl text-white/80 max-w-2xl mb-8">{content.message}</p>
    <div className="rounded-xl bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/40 px-8 py-4">
      <p className="text-2xl font-bold text-white">{content.cta}</p>
    </div>
  </div>
);

// ===================== Main Slide Renderer =====================
const SlideRenderer = ({ slide }) => {
  const renderers = {
    title: TitleSlide,
    discovery: DiscoverySlide,
    background: BackgroundSlide,
    tools: ToolsSlide,
    rapidResponse: RapidResponseSlide,
    workflow: WorkflowSlide,
    aiAssistant: AIAssistantSlide,
    smsPlatform: SMSPlatformSlide,
    reviewBoost: ReviewBoostSlide,
    callTracking: CallTrackingSlide,
    scorecard: ScorecardSlide,
    multiScorecard: MultiScorecardSlide,
    coaching: CoachingSlide,
    demo: DemoSlide,
    closing: ClosingSlide
  };
  const Component = renderers[slide.type];
  return Component ? <Component content={slide.content} /> : null;
};

// ===================== Main Page =====================
export default function PresentationsCalldripDemo() {
  const [current, setCurrent] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const next = useCallback(() => setCurrent((c) => Math.min(c + 1, slides.length - 1)), []);
  const prev = useCallback(() => setCurrent((c) => Math.max(c - 1, 0)), []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "f" || e.key === "F") toggleFullscreen();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [next, prev, toggleFullscreen]);

  const slide = slides[current];

  return (
    <div className="min-h-screen bg-[#0a1628] relative overflow-hidden" data-testid="calldrip-demo-presentation">
      {/* Ambient glow */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative h-screen flex flex-col">
        {/* Slide */}
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full h-full max-w-[1600px] mx-auto">
            <SlideRenderer slide={slide} />
          </div>
        </div>

        {/* Nav arrows */}
        <button
          onClick={prev}
          disabled={current === 0}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 flex items-center justify-center text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          data-testid="prev-slide-btn"
        >
          <ChevronLeft size={24} />
        </button>
        <button
          onClick={next}
          disabled={current === slides.length - 1}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 flex items-center justify-center text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          data-testid="next-slide-btn"
        >
          <ChevronRight size={24} />
        </button>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 px-6 py-4 flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <span className="text-white/60" data-testid="slide-counter">Slide {current + 1} of {slides.length}</span>
            <div className="flex gap-1.5">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`h-1.5 rounded-full transition-all ${i === current ? "w-8 bg-cyan-400" : "w-1.5 bg-white/30 hover:bg-white/50"}`}
                  data-testid={`slide-dot-${i + 1}`}
                />
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <PresentationDownloadButton
              slides={slides}
              renderSlide={(s) => <SlideRenderer slide={s} />}
              filename="Calldrip-Demo"
              bgColor="#0a1628"
            />
            <span className="text-white/40 text-xs hidden lg:inline">
              Press <kbd className="bg-white/10 px-1.5 py-0.5 rounded">F</kbd> for fullscreen | <kbd className="bg-white/10 px-1 py-0.5 rounded">←</kbd> <kbd className="bg-white/10 px-1 py-0.5 rounded">→</kbd> to navigate
            </span>
            <button
              onClick={toggleFullscreen}
              className="bg-cyan-500 hover:bg-cyan-400 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all"
              data-testid="present-btn"
            >
              {isFullscreen ? "Exit" : "Present"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
